import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { PlatformLogo, PLATFORM_LABEL, platformKey } from './CampaignsPage';
import CurrencyValue from './CurrencyValue';
import { useTranslation } from 'react-i18next';
import i18n, { targetingLabel as tv, currentLocale } from '../i18n';
import '../styles/sensemax-dashboard.css';

// ── helpers ──────────────────────────────────────────────────────────────────
// The SM_<Platform>_<Tool>_<Name>_<rand> name is a service label — show the clean name.
const displayName = (raw: string): string => {
  if (!raw) return '';
  if (!raw.startsWith('SM_')) return raw;
  const m = raw.match(/^SM_[^_]+_[^_]+_(.+?)(?:_\d{6,})+$/);
  const base = m ? m[1] : raw;
  return base.replace(/([\p{Ll}\p{N}])(\p{Lu})/gu, '$1 $2');
};
const fmtInt = (n: number) => (n || 0).toLocaleString(currentLocale());
// Money renders with the currency symbol small + muted (like everywhere else).
const fmtMoney = (n: number, ccy: string): React.ReactNode =>
  <CurrencyValue value={n || 0} currency={ccy} />;
const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString(currentLocale(), { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

// Each platform reports its own breakdowns; render bars for compact categorical
// splits and tables for long-tail dimensions.
const BREAKDOWN_LABEL_KEY: Record<string, string> = {
  age: 'smd.bdAge', gender: 'smd.bdGender', geo: 'smd.bdGeo', device: 'smd.bdDevice',
  network: 'smd.bdNetwork', placement: 'smd.bdPlacement', creative: 'smd.bdCreative',
  url: 'smd.bdUrl', keyword: 'smd.bdKeyword',
};
// Dimensions whose row keys are OUR OWN enum values (Male, Computer, 18-24…) and so
// have a catalogue entry. Geo names, URLs, keywords and creative names come from the
// platform verbatim and are shown as they arrive.
const TRANSLATED_DIMS = new Set(['age', 'gender', 'device', 'network', 'placement']);
const BREAKDOWN_ORDER = ['age', 'gender', 'geo', 'device', 'network', 'placement', 'creative', 'url', 'keyword'];

// ── charts ───────────────────────────────────────────────────────────────────
// Brand family but spaced for contrast (adjacent segments stay distinct);
// a white stroke between segments does most of the separation work.
const DONUT_COLORS = ['#FF6B4A', '#E94B6C', '#9B4DA8', '#F79A3E', '#D44B80', '#8E3A9A', '#F0574F'];
const polar = (cx: number, cy: number, r: number, deg: number): [number, number] => {
  const a = (deg - 90) * Math.PI / 180; return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
};
const arcPath = (cx: number, cy: number, r: number, ri: number, start: number, end: number) => {
  if (end - start >= 359.999) end = start + 359.999;
  const [x1, y1] = polar(cx, cy, r, end), [x2, y2] = polar(cx, cy, r, start);
  const [x3, y3] = polar(cx, cy, ri, start), [x4, y4] = polar(cx, cy, ri, end);
  const large = end - start > 180 ? 1 : 0;
  return `M${x1} ${y1} A${r} ${r} 0 ${large} 0 ${x2} ${y2} L${x3} ${y3} A${ri} ${ri} 0 ${large} 1 ${x4} ${y4} Z`;
};
const DonutChart: React.FC<{ rows: any[]; lbl: (s: string) => string }> = ({ rows, lbl }) => {
  const data = rows.filter(r => r.impressions > 0).slice(0, 7);
  const total = data.reduce((s, r) => s + r.impressions, 0) || 1;
  let acc = 0;
  const segs = data.map((r, i) => {
    const start = acc / total * 360; acc += r.impressions; const end = acc / total * 360;
    return { ...r, start, end, pct: r.impressions / total * 100, color: DONUT_COLORS[i % DONUT_COLORS.length] };
  });
  const single = segs.length === 1;
  return (
    <div className="smd-donut-wrap">
      <svg viewBox="0 0 120 120" className="smd-donut">
        {single
          ? <circle cx="60" cy="60" r="43.5" fill="none" stroke={segs[0].color} strokeWidth="21" />
          : segs.map((s, i) => <path key={i} d={arcPath(60, 60, 54, 33, s.start, s.end)} fill={s.color} stroke="#fff" strokeWidth="2.5" strokeLinejoin="round" />)}
      </svg>
      <div className="smd-legend">
        {segs.map((s, i) => (
          <div className="smd-leg" key={i}><span className="smd-leg-dot" style={{ background: s.color }} />{lbl(s.key)}<em>{s.pct.toFixed(0)}%</em></div>
        ))}
      </div>
    </div>
  );
};
const genderColor = (key: string) => {
  const g = (key || '').toLowerCase();
  return g.includes('female') ? '#FF6B4A' : g.includes('male') ? '#8E3A9A' : '#c9c9d2';
};
const GenderSplit: React.FC<{ rows: any[]; lbl: (s: string) => string }> = ({ rows, lbl }) => {
  const data = rows.filter(r => r.impressions > 0);
  const total = data.reduce((s, r) => s + r.impressions, 0) || 1;
  return (
    <div className="smd-gender">
      <div className="smd-gender-bar">
        {data.map((r, i) => {
          const pct = r.impressions / total * 100;
          return <div key={i} className="smd-gender-seg" style={{ width: `${pct}%`, background: genderColor(r.key) }}>{pct > 9 ? `${pct.toFixed(0)}%` : ''}</div>;
        })}
      </div>
      <div className="smd-legend">
        {data.map((r, i) => (
          <div className="smd-leg" key={i}><span className="smd-leg-dot" style={{ background: genderColor(r.key) }} />{lbl(r.key)}<em>{(r.impressions / total * 100).toFixed(0)}%</em></div>
        ))}
      </div>
    </div>
  );
};

// Full per-segment metrics — every breakdown row carries CTR / CPC / CPM / Conv / CPA / Spend.
const MetricsTable: React.FC<{ title: string; rows: any[]; ccy: string; lbl: (s: string) => string }> = ({ title, rows, ccy, lbl }) => (
  <div className="smd-table-wrap">
    <table className="smd-table smd-bd-table">
      <thead>
        <tr>
          <th>{title}</th><th>{i18n.t('smd.colImpr')}</th><th>{i18n.t('smd.colClicks')}</th><th>CTR</th>
          <th>CPC</th><th>CPM</th><th>{i18n.t('smd.colConv')}</th><th>CPA</th><th>{i18n.t('smd.colSpend')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, 15).map((r, i) => (
          <tr key={i}>
            <td title={r.key} className="smd-key-cell">{lbl(r.key)}</td>
            <td>{fmtInt(r.impressions)}</td>
            <td>{fmtInt(r.clicks)}</td>
            <td>{(r.ctr || 0).toFixed(2)}%</td>
            <td>{fmtMoney(r.cpc || 0, r.currency || ccy)}</td>
            <td>{fmtMoney(r.cpm || 0, r.currency || ccy)}</td>
            <td>{r.conversions == null ? '—' : fmtInt(r.conversions)}</td>
            <td>{r.cpa ? fmtMoney(r.cpa, r.currency || ccy) : '—'}</td>
            <td>{fmtMoney(r.spend, r.currency || ccy)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Horizontal share bars (device / network / placement / geo) — the visual above the table.
const BarViz: React.FC<{ rows: any[]; lbl: (s: string) => string }> = ({ rows, lbl }) => {
  const top = rows.slice(0, 8);
  const max = Math.max(...top.map(r => r.impressions), 1);
  const totalImpr = rows.reduce((s, r) => s + (r.impressions || 0), 0) || 1;
  return (
    <div className="smd-bars">
      {top.map((r, i) => (
        <div className="smd-bar-row" key={i}>
          <span className="smd-bar-label" title={r.key}>{lbl(r.key)}</span>
          <div className="smd-bar-track"><div className="smd-bar-fill" style={{ width: `${Math.max(2, r.impressions / max * 100)}%` }} /></div>
          <span className="smd-bar-val">{fmtInt(r.impressions)} <em>{(r.impressions / totalImpr * 100).toFixed(0)}%</em></span>
        </div>
      ))}
    </div>
  );
};
const DONUT_VIZ = new Set(['age', 'device', 'network', 'placement']);

const BreakdownBlock: React.FC<{ name: string; rows: any[]; ccy: string }> = ({ name, rows, ccy }) => {
  if (!rows || !rows.length) return null;
  const title = BREAKDOWN_LABEL_KEY[name] ? i18n.t(BREAKDOWN_LABEL_KEY[name]) : name;
  const lbl = TRANSLATED_DIMS.has(name) ? tv : (s: string) => s;
  return (
    <div className="smd-bd smd-bd-full">
      <div className="smd-bd-title">{title}</div>
      {DONUT_VIZ.has(name) && <DonutChart rows={rows} lbl={lbl} />}
      {name === 'gender' && <GenderSplit rows={rows} lbl={lbl} />}
      {name === 'geo' && <BarViz rows={rows} lbl={lbl} />}
      <MetricsTable title={title} rows={rows} ccy={ccy} lbl={lbl} />
    </div>
  );
};

// ── metric chart ─────────────────────────────────────────────────────────────
const METRIC_META: Record<string, { labelKey?: string; money?: boolean; pct?: boolean }> = {
  impressions: { labelKey: 'smd.mImpressions' }, clicks: { labelKey: 'smd.mClicks' }, ctr: { pct: true },
  conversions: { labelKey: 'smd.mConversions' }, cpc: { money: true }, cpm: { money: true },
  cpa: { money: true }, spend: { labelKey: 'smd.mSpend', money: true },
};
const chartFmt = (v: number, metric: string, ccy: string): React.ReactNode => {
  const meta = METRIC_META[metric] || {};
  return meta.money ? fmtMoney(v, ccy) : meta.pct ? `${v.toFixed(2)}%` : fmtInt(v);
};
const shortDate = (d: string) => { const [, m, day] = d.split('-'); return `${day}.${m}`; };
// Compact axis number (no currency symbol — the tiles/tooltip carry it).
const axisFmt = (v: number, metric: string): string => {
  const meta = METRIC_META[metric] || {};
  if (meta.pct) return `${v.toFixed(1)}%`;
  const a = Math.abs(v);
  if (a >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return String(Math.round(v));
};
// Smooth line through points (Catmull-Rom → cubic bezier).
const smoothLine = (pts: readonly (readonly [number, number])[]): string => {
  if (!pts.length) return '';
  if (pts.length < 3) return pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  let d = `M${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6, c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6, c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C${c1x.toFixed(1)} ${c1y.toFixed(1)} ${c2x.toFixed(1)} ${c2y.toFixed(1)} ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }
  return d;
};

// ── optimization events (MOCKUP data for now; real logging wired later) ──────
const OPTIM_TYPES: Record<string, { color: string; labelKey: string; icon: string }> = {
  budget_up:  { color: '#FF7A2F', labelKey: 'smd.optBudgetUp',   icon: '↑' },
  budget_down:{ color: '#E9A23B', labelKey: 'smd.optBudgetDown', icon: '↓' },
  pause:      { color: '#E94B6C', labelKey: 'smd.optPause',      icon: '⏸' },
  resume:     { color: '#1e9e5a', labelKey: 'smd.optResume',     icon: '▶' },
  creative:   { color: '#8E3A9A', labelKey: 'smd.optCreative',   icon: '✦' },
  targeting:  { color: '#3b82f6', labelKey: 'smd.optTargeting',  icon: '◎' },
  bid:        { color: '#14b8a6', labelKey: 'smd.optBid',        icon: '$' },
};
// Real optimization events (populated once server-side logging of AI/guard actions
// is wired). Empty until then — no mock data.
const OPTIM_EVENTS: any[] = [];

const OptimizationTimeline: React.FC<{ events: any[]; active: number | null; onHover: (i: number | null) => void }> =
  ({ events, active, onHover }) => {
  if (!events.length) return (
    <div className="smd-optim-tl">
      <div className="smd-optim-tl-title">{i18n.t('smd.optimEvents')}</div>
      <div className="smd-optim-empty">{i18n.t('smd.noOptimEvents')}</div>
    </div>
  );
  return (
    <div className="smd-optim-tl">
      <div className="smd-optim-tl-title">{i18n.t('smd.optimEvents')}</div>
      <div className="smd-optim-tl-scroll">
        <table className="smd-ev-table">
          <tbody>
            {events.map((e, i) => {
              const ev = OPTIM_TYPES[e.type];
              return (
                <tr key={i} className={active === i ? 'active' : ''}
                  onMouseEnter={() => onHover(i)} onMouseLeave={() => onHover(null)}>
                  <td className="smd-ev-date" style={{ borderLeftColor: ev?.color || '#8E3A9A' }}>{shortDate(e.date)}</td>
                  <td className="smd-ev-type">{ev ? i18n.t(ev.labelKey) : ''}</td>
                  <td className="smd-ev-text" title={e.text}>{e.text}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Full-history line chart for one metric. `markers` = optimization events (vertical lines).
const MetricChart: React.FC<{ series: any[]; metric: string; ccy: string; markers?: any[]; activeMarker?: number | null; onMarkerHover?: (i: number | null) => void }> =
  ({ series, metric, ccy, markers = [], activeMarker = null, onMarkerHover }) => {
  const [hover, setHover] = useState<number | null>(null);
  if (!series || !series.length) return <div className="smd-empty">{i18n.t('smd.noTrendData')}</div>;
  const vals = series.map(d => Number(d[metric]) || 0);
  const max = Math.max(...vals, 0);
  const min = Math.min(...vals, 0);
  const rng = max - min || 1;
  const W = 1000, H = 240, padT = 14, padB = 8, padX = 4;
  const iw = W - padX * 2, ih = H - padT - padB;
  const xAt = (i: number) => padX + (series.length <= 1 ? iw / 2 : (i / (series.length - 1)) * iw);
  const yAt = (v: number) => padT + ih - ((v - min) / rng) * ih;
  const pts = vals.map((v, i) => [xAt(i), yAt(v)] as const);
  const line = smoothLine(pts);
  const area = `${line} L${xAt(series.length - 1).toFixed(1)} ${padT + ih} L${xAt(0).toFixed(1)} ${padT + ih} Z`;
  const grid = [0, 0.25, 0.5, 0.75, 1];
  const pct = (n: number, total: number) => `${(n / total) * 100}%`;
  const nx = Math.min(7, series.length);
  const xLabelIdx = nx <= 1 ? [0]
    : Array.from(new Set(Array.from({ length: nx }, (_, i) => Math.round(i * (series.length - 1) / (nx - 1)))));
  return (
    <div className="smd-chart">
      <div className="smd-chart-plot">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="smd-chart-svg">
          <defs>
            <linearGradient id="smc-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#FF6B4A" stopOpacity="0.22" /><stop offset="1" stopColor="#8E3A9A" stopOpacity="0" /></linearGradient>
            <linearGradient id="smc-line" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#FF6B4A" /><stop offset="0.5" stopColor="#E94B6C" /><stop offset="1" stopColor="#8E3A9A" /></linearGradient>
          </defs>
          {grid.map((t, i) => <line key={i} x1={padX} x2={W - padX} y1={padT + ih * t} y2={padT + ih * t} stroke="#f0eef4" strokeWidth="1" />)}
          {markers.map((mk, i) => {
            const idx = series.findIndex(d => d.date === mk.date);
            if (idx < 0) return null;
            const t = OPTIM_TYPES[mk.type] || { color: '#8E3A9A' };
            const on = activeMarker === i;
            return <line key={`mk${i}`} x1={xAt(idx)} x2={xAt(idx)} y1={padT} y2={padT + ih}
              stroke={t.color} strokeWidth={on ? 2 : 1} strokeDasharray="4 3" opacity={on ? 0.95 : 0.4}
              vectorEffect="non-scaling-stroke" />;
          })}
          <path d={area} fill="url(#smc-fill)" />
          <path d={line} fill="none" stroke="url(#smc-line)" strokeWidth="2.5" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          {series.map((_, i) => <rect key={i} x={xAt(i) - iw / series.length / 2} y={0} width={iw / series.length} height={H} fill="transparent" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />)}
        </svg>
        {/* y-axis labels (compact, no currency symbol) */}
        <div className="smd-chart-yaxis">
          {grid.map((t, i) => <span key={i} style={{ top: pct(padT + ih * t, H) }}>{axisFmt(min + rng * (1 - t), metric)}</span>)}
        </div>
        {/* hover dot (HTML, so it stays circular) */}
        {hover != null && (
          <div className="smd-chart-dot" style={{ left: pct(xAt(hover), W), top: pct(yAt(vals[hover]), H) }} />
        )}
        {/* tooltip */}
        {hover != null && (
          <div className="smd-chart-tip" style={{ left: pct(xAt(hover), W), top: pct(yAt(vals[hover]), H) }}>
            <div className="smd-tip-date">{series[hover].date.split('-').reverse().join('.')}</div>
            <div className="smd-tip-val">{chartFmt(vals[hover], metric, ccy)}</div>
          </div>
        )}
        {/* optimization event markers — dot at the top of each line */}
        {markers.map((mk, i) => {
          const idx = series.findIndex(d => d.date === mk.date);
          if (idx < 0) return null;
          const t = OPTIM_TYPES[mk.type] || { color: '#8E3A9A', icon: '•' };
          return <div key={i} className={`smd-mk-dot ${activeMarker === i ? 'active' : ''}`}
            style={{ left: pct(xAt(idx), W), background: t.color }}
            onMouseEnter={() => onMarkerHover?.(i)} onMouseLeave={() => onMarkerHover?.(null)}>{t.icon}</div>;
        })}
        {/* hover popover for the active marker */}
        {activeMarker != null && markers[activeMarker] && (() => {
          const mk = markers[activeMarker];
          const idx = series.findIndex(d => d.date === mk.date);
          if (idx < 0) return null;
          const ev = OPTIM_TYPES[mk.type];
          return (
            <div className="smd-mk-pop" style={{ left: pct(xAt(idx), W) }}>
              <span className="smd-mk-pop-dot" style={{ background: ev?.color || '#8E3A9A' }}>{ev?.icon || '•'}</span>
              <div className="smd-mk-pop-body">
                <div className="smd-mk-pop-date">{mk.date.split('-').reverse().join('.')} · {ev ? i18n.t(ev.labelKey) : ''}</div>
                <div className="smd-mk-pop-text">{mk.text}</div>
              </div>
            </div>
          );
        })()}
      </div>
      <div className="smd-chart-xaxis">
        {xLabelIdx.map((idx) => (
          <span key={idx} style={{ left: pct(xAt(idx), W), transform: `translateX(${idx === 0 ? '0' : idx === series.length - 1 ? '-100%' : '-50%'})` }}>{shortDate(series[idx].date)}</span>
        ))}
      </div>
    </div>
  );
};

const MetricTiles: React.FC<{ m: any; ccy: string; onSelect?: (metric: string) => void; selected?: string }> = ({ m, ccy, onSelect, selected }) => {
  const c = m?.currency || ccy;
  const cells: [string, string, React.ReactNode][] = [
    ['impressions', i18n.t('smd.mImpressions'), fmtInt(m?.impressions || 0)],
    ['clicks', i18n.t('smd.mClicks'), fmtInt(m?.clicks || 0)],
    ['ctr', 'CTR', `${(m?.ctr || 0).toFixed(2)}%`],
    ['conversions', i18n.t('smd.mConversions'), m?.conversions == null ? '—' : fmtInt(m.conversions)],
    ['cpc', 'CPC', fmtMoney(m?.cpc || 0, c)],
    ['cpm', 'CPM', fmtMoney(m?.cpm || 0, c)],
    ['cpa', 'CPA', m?.cpa ? fmtMoney(m.cpa, c) : '—'],
    ['spend', i18n.t('smd.mSpend'), fmtMoney(m?.spend || 0, c)],
  ];
  return (
    <div className="smd-tiles smd-tiles-8">
      {cells.map(([k, l, v]) => (
        <div className={`smd-tile ${onSelect ? 'clickable' : ''} ${selected === k ? 'selected' : ''}`} key={k}
          onClick={onSelect ? () => onSelect(k) : undefined}>
          <div className="smd-tl">{l}</div><div className="smd-tv">{v}</div>
        </div>
      ))}
    </div>
  );
};

const FormatCard: React.FC<{ fmt: any; ccy: string; commissionPct?: number; external?: boolean }> = ({ fmt, ccy, commissionPct = 0, external = false }) => {
  const bds: Record<string, any[]> = fmt.breakdowns || {};
  const keys = BREAKDOWN_ORDER.filter(k => bds[k]).concat(Object.keys(bds).filter(k => !BREAKDOWN_ORDER.includes(k)));
  const commission = external ? (fmt.total?.spend || 0) * commissionPct / 100 : 0;
  return (
    <div className="smd-format">
      <div className="smd-format-head">
        <span className="smd-format-name">{displayName(fmt.name)}</span>
        {fmt.format && <span className="smd-format-badge">{fmt.format}</span>}
        {fmt.status && <span className={`smd-format-status ${String(fmt.status).toLowerCase()}`}>{String(fmt.status).replace(/_/g, ' ').toLowerCase()}</span>}
        {external && (
          <span className="smd-format-commission">{i18n.t('smd.colCommission')} {fmtMoney(commission, ccy)}</span>
        )}
      </div>
      <MetricTiles m={fmt.total} ccy={ccy} />
      {keys.length > 0 && <div className="smd-bd-grid">{keys.map(k => <BreakdownBlock key={k} name={k} rows={bds[k]} ccy={ccy} />)}</div>}
    </div>
  );
};

interface Campaign {
  id: string; name: string; advertiser?: string; status?: string;
  impressions?: number; clicks?: number; ctr?: number; spend?: number;
  budget?: number; budget_currency?: string | null;
  start_date?: string | null; end_date?: string | null;
  types?: string[]; trend?: any[];
  sense_max_platforms?: string[]; sense_max_draft_id?: number;
  bg_pacing_state?: string | null;
}

// Turn a raw per-tool launch error into a human line. Platform errors arrive as
// "[meta:create_adgroup] HTTP 400: {json…}" — pull the platform's own user-facing message
// (Meta's error_user_title/msg, else `message`); non-JSON errors just lose the noisy prefix.
// A platform's raw reply is for the log, not for the panel. Pull out the one sentence
// that says what went wrong; a JSON body, an HTTP prefix or an enum dump never reaches
// the screen. The untouched text stays in the row's tooltip.
const humanizeLaunchError = (raw?: string | null): string => {
  if (!raw) return i18n.t('smd.launchFailed');
  const tidy = (s: string): string => {
    let t = String(s).trim()
      .replace(/^\[[^\]]+\]\s*(HTTP\s*\d+\s*:)?\s*/i, '')   // our own "[meta:create_ad] HTTP 400:"
      .replace(/^\(#\d+\)\s*/, '')                          // Meta's "(#100) "
      .replace(/^error\s+\d+\s*[:—-]\s*/i, '');             // Yandex's "error 8000: "
    // "<field> must be one of the following values: A, B, C, …" — keep the field, drop the list.
    const enumAt = t.search(/\bmust be one of\b/i);
    if (enumAt >= 0) t = i18n.t('smd.platformRejects', { what: t.slice(0, enumAt).trim() });
    return t.replace(/\s+/g, ' ').slice(0, 240);
  };

  const at = raw.indexOf('{');
  if (at >= 0) {
    try {
      const parsed = JSON.parse(raw.slice(at)) as any;
      const e = parsed.error || parsed;
      // Meta writes a message meant for humans — prefer it over the developer one.
      if (e.error_user_title && e.error_user_msg && e.error_user_title !== e.error_user_msg)
        return tidy(`${e.error_user_title}: ${e.error_user_msg}`);
      if (e.error_user_msg || e.error_user_title) return tidy(e.error_user_msg || e.error_user_title);
      // Google Ads says only "Request contains an invalid argument" up top; what actually
      // happened sits in details[].errors[].
      const inner: any[] = (e.details || []).flatMap((d: any) => d.errors || []);
      if (inner.length) {
        const first = inner[0];
        const code = Object.values(first.errorCode || {})[0];
        const msg = first.message || e.message || '';
        const readable = code ? String(code).toLowerCase().replace(/_/g, ' ') : '';
        // The code is only worth showing when it adds something the sentence doesn't.
        const same = (s: string) => s.toLowerCase().replace(/[^a-z]/g, '');
        const add = readable && msg && !same(msg).includes(same(readable));
        return tidy(add ? `${msg} (${readable})` : msg || readable);
      }
      if (e.message) return tidy(e.message);
    } catch { /* not JSON after all — fall through to the plain text */ }
  }
  return tidy(raw);
};

const SenseMaxDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { campaignId } = useParams<{ campaignId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState<Campaign | null>(
    (location.state as any)?.campaign || null
  );
  // Platforms excluded at launch because the total budget couldn't fund their minimum even
  // after taking share from the others (last-resort — shown so the user knows why + how to fix).
  const excludedPlatforms: Array<{ platform?: string; channel: string; reason?: string }> =
    (location.state as any)?.excluded || [];
  const [loading, setLoading] = useState(!campaign);
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [stats, setStats] = useState<any>(null);           // { total, by_platform:[{platform,...}] }
  const [statsLoading, setStatsLoading] = useState(false);
  const [budget, setBudget] = useState<any>(null);         // { external_spend, commission, model_type, ... }
  const [trend, setTrend] = useState<any>(null);           // { aggregate:[...], by_channel:{...} }
  const [chartMetric, setChartMetric] = useState<string>('impressions');
  const [activeMarker, setActiveMarker] = useState<number | null>(null);

  // edit modals
  const [editKind, setEditKind] = useState<null | 'budget' | 'dates'>(null);
  const [fBudget, setFBudget] = useState('');
  const [fStart, setFStart] = useState('');
  const [fEnd, setFEnd] = useState('');
  const [saving, setSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // Per-format launch status (polled live; no page reload). draftId comes from the
  // route (/campaigns/smdraft-<id>/dashboard) or the loaded campaign.
  const draftId = useMemo<number | null>(() => {
    if (campaignId && campaignId.startsWith('smdraft-')) return Number(campaignId.slice(8)) || null;
    return (campaign as any)?.sense_max_draft_id || null;
  }, [campaignId, campaign]);
  const isDraftLaunch = !!(campaignId && campaignId.startsWith('smdraft-'));
  const [launchJobs, setLaunchJobs] = useState<any[] | null>(null);
  const [retrying, setRetrying] = useState<number | null>(null);
  const [pollNonce, setPollNonce] = useState(0);

  // Fetch campaign if arrived by direct URL (no nav state)
  useEffect(() => {
    if (campaign || !campaignId) return;
    setLoading(true);
    api.get(`/api/reports/campaigns/list`)
      .then(r => {
        const found = (r.data?.campaigns || []).find((c: any) => String(c.id) === String(campaignId));
        setCampaign(found || null);
      })
      .catch(() => setCampaign(null))
      .finally(() => setLoading(false));
  }, [campaignId, campaign]);

  // Poll per-format launch status until every job is terminal. Retrying bumps pollNonce
  // to resume the loop. No page reload — the dashboard updates in place.
  useEffect(() => {
    if (!draftId) return;
    let stop = false; let timer: any;
    const poll = async () => {
      try {
        const r = await api.get(`/api/sense-max/launch/jobs?draft_id=${draftId}`);
        if (stop) return;
        const jobs = r.data?.jobs || [];
        setLaunchJobs(jobs);
        // Keep polling while a launch OR a pause/resume (control) is still in flight, so the
        // per-tool status indicators update live inside this same panel.
        const launching = jobs.some((j: any) => j.status === 'pending' || j.status === 'in_progress');
        const controlling = jobs.some((j: any) => j.control_status === 'pending' || j.control_status === 'in_progress');
        if (jobs.length > 0 && (launching || controlling)) timer = setTimeout(poll, 1500);
        else setStatusBusy(false);
      } catch { if (!stop) timer = setTimeout(poll, 4000); }
    };
    poll();
    return () => { stop = true; if (timer) clearTimeout(timer); };
  }, [draftId, pollNonce]);

  const retryJob = async (jobId: number) => {
    setRetrying(jobId);
    try {
      await api.post(`/api/sense-max/launch/jobs/${jobId}/retry`);
      setLaunchJobs(js => (js || []).map(j => j.id === jobId ? { ...j, status: 'pending', error: null } : j));
      setPollNonce(n => n + 1);   // resume polling
    } catch (e: any) {
      setToast({ kind: 'err', text: e?.response?.data?.detail || t('smd.errRetry') });
    } finally { setRetrying(null); }
  };

  // Fetch cross-platform stats (skip for a fresh draft-launch — no real campaign id yet)
  useEffect(() => {
    if (!campaignId || (campaignId && campaignId.startsWith('smdraft-'))) return;
    setStatsLoading(true);
    api.get(`/api/sense-max/campaign/${campaignId}/stats`)
      .then(r => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));
    api.get(`/api/sense-max/campaign/${campaignId}/budget`)
      .then(r => setBudget(r.data))
      .catch(() => setBudget(null));
    api.get(`/api/sense-max/campaign/${campaignId}/trend`)
      .then(r => setTrend(r.data))
      .catch(() => setTrend(null));
  }, [campaignId]);

  // Currency, authoritative: the /budget endpoint returns the CLIENT currency (get_advertiser_
  // _currency); for a fresh draft-launch (budget skipped) we resolve it from the catalog. Never
  // silently default to EUR for a non-EUR client (e.g. a KZ office → KZT).
  // Currency is bound to the CAMPAIGN's OWNER advertiser (never the currently-selected client,
  // so switching clients in the sidebar must NOT change it). Resolve the advertiser from the
  // campaign, or — for a fresh draft-launch with no advertiser on the object — from its draft.
  const [clientCcy, setClientCcy] = useState<string | null>(null);
  useEffect(() => {
    setClientCcy(null);
    let cancelled = false;
    (async () => {
      let adv = (campaign as any)?.advertiser_id;
      if (!adv && draftId) {
        try { const d = await api.get(`/api/sense-max/drafts/${draftId}`); adv = d.data?.advertiser_id; } catch { /* */ }
      }
      if (cancelled || !adv || adv === '__all__') return;
      try {
        const r = await api.get('/api/sense-max/catalog', { params: { advertiser_id: adv } });
        if (!cancelled) setClientCcy(r.data?.currency || null);
      } catch { /* */ }
    })();
    return () => { cancelled = true; };
  }, [campaign, draftId]);
  // All money is already CONVERTED to the client currency server-side (reports _convert_spend;
  // budget/stats endpoints convert per-channel → client_ccy). ccy just picks the label, from
  // campaign-bound sources only: /budget endpoint currency → catalog(advertiser) → EUR.
  const ccy = budget?.currency || clientCcy || campaign?.budget_currency || 'EUR';
  const platforms = useMemo(() => campaign?.sense_max_platforms?.length
    ? campaign.sense_max_platforms : ['sense'], [campaign]);
  // Platforms whose cabinet is now disconnected (from the launch jobs) — greyed, not hidden.
  const disconnectedPlats = useMemo(() => {
    const CH2P: Record<string, string> = { google_ads: 'google', meta: 'meta', tiktok: 'tiktok', yandex: 'yandex', linkedin: 'linkedin', sense: 'sense' };
    const s = new Set<string>();
    (launchJobs || []).forEach((j: any) => { if (j.disconnected) s.add(CH2P[j.channel] || j.channel); });
    return s;
  }, [launchJobs]);
  // A campaign that has already started can't have its start moved (every platform rejects it).
  const startAlreadyBegun = !!(campaign?.start_date &&
    String(campaign.start_date).slice(0, 10) <= new Date().toISOString().slice(0, 10));

  const openEdit = (kind: 'budget' | 'dates') => {
    setToast(null);
    // Prefill the TOTAL campaign budget (the full client amount that gets split across
    // platforms), not the Sense leg's 35% share — /manage re-splits from this total.
    if (kind === 'budget') {
      const total = budget?.total_budget ?? campaign?.budget;
      setFBudget(total != null ? String(total) : '');
    }
    if (kind === 'dates') { setFStart(campaign?.start_date || ''); setFEnd(campaign?.end_date || ''); }
    setEditKind(kind);
  };

  const saveEdit = async () => {
    if (!campaign) return;
    setSaving(true);
    setToast(null);
    try {
      const body: any = { advertiser_id: (campaign as any).advertiser_id, campaign_id: campaign.id };
      if (editKind === 'budget') body.budget = Number(fBudget);
      if (editKind === 'dates') { body.start_date = fStart || null; body.end_date = fEnd || null; }
      const r = await api.post('/api/sense-max/manage', body);
      setCampaign(c => c ? {
        ...c,
        ...(editKind === 'dates' ? { end_date: fEnd, ...(r.data?.start_blocked ? {} : { start_date: fStart }) } : {}),
      } : c);
      // Budget display reads budget.total_budget — reflect the new total right away.
      if (editKind === 'budget') setBudget((b: any) => b ? { ...b, total_budget: Number(fBudget) } : b);
      setEditKind(null);
      if (r.data?.async) {
        // Per-tool progress is shown in the Launch status panel — no extra toast needed.
        setPollNonce(n => n + 1);
      } else if (editKind === 'budget') {
        setToast({ kind: 'ok', text: t('smd.budgetSaved') });
      }
    } catch (e: any) {
      setToast({ kind: 'err', text: e?.response?.data?.detail || t('smd.errApplyChange') });
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async () => {
    if (!campaign) return;
    const next = campaign.status === 'active' ? 'paused' : 'active';
    setStatusBusy(true);
    setToast(null);
    try {
      const r = await api.post(`/api/sense-max/campaign/${campaign.id}/status`, { status: next });
      setCampaign(c => c ? { ...c, status: next } : c);
      if (r.data?.async) {
        // Per-tool progress is shown INSIDE the existing Launch status panel (control_status
        // on each job). Resume that panel's poll; it keeps ticking until every tool settles.
        setPollNonce(n => n + 1);
      } else {
        setStatusBusy(false);
      }
    } catch (e: any) {
      setStatusBusy(false);
      const d = e?.response?.data?.detail;
      let text = t('smd.errChangeStatus');
      if (typeof d === 'string') {
        text = d;
      } else if (d && d.code === 'insufficient_balance') {
        const ccy = d.currency || '';
        const money = (v: number) =>
          `${Number(v || 0).toLocaleString(currentLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`.trim();
        text = t('smd.notEnoughResume', { need: money(d.need), have: money(d.have) });
      }
      setToast({ kind: 'err', text });
    }
  };

  // Manage creatives/texts of a launched campaign in the Sense Max constructor (it already
  // has the format gallery + readiness gate + full text schema). Opened in "manage" mode so
  // its action becomes Apply-changes (replace) instead of a fresh launch.
  const openReplace = () => {
    const did = campaign?.sense_max_draft_id;
    if (!did) {
      setToast({ kind: 'err', text: t('smd.noDraftLinked') });
      return;
    }
    navigate(`/sense-max?draft=${did}&manage=1`);
  };

  if (loading) return <div className="smd-loading">{t('smd.loadingDashboard')}</div>;
  if (!campaign) return (
    <div className="smd-loading">
      {t('smd.campaignNotFound')} <button className="smd-link" onClick={() => navigate('/campaigns')}>{t('smd.backToCampaigns')}</button>
    </div>
  );

  const statusLabel = t(campaign.status === 'active' ? 'smd.stLive'
    : campaign.status === 'paused' ? 'smd.stPaused'
    : campaign.status === 'ended' ? 'smd.stEnded' : 'smd.stDraft');
  // Sense Max runs only with ≥2 launched tools (Sense + ≥1 external). With fewer,
  // Resume is disabled here; the backend also rejects it (incl. a balance check).
  const launchedCount = (launchJobs || []).filter((j: any) => j.status === 'launched').length;
  const canResume = launchedCount >= 2;

  // aggregate tiles (Sense leg today; cross-platform once collectors populate stats)
  const agg = stats?.total || {
    impressions: campaign.impressions || 0, clicks: campaign.clicks || 0,
    ctr: campaign.ctr || 0, spend: campaign.spend || 0,
  };
  const byPlatform: any[] = stats?.by_platform || [];
  // The cross-platform SPEND tile is the TOTAL campaign cost to the client — media (Sense +
  // external) PLUS our commission (АК). agg.spend is media-only, so add the commission from
  // the billing breakdown for the "All platforms" view (per-channel tiles stay media-only).
  const aggWithCommission = { ...agg, spend: (agg.spend || 0) + (budget?.commission || 0) };

  return (
    <div className="smd-page">
      {/* Header */}
      <div className="smd-top">
        <button className="smd-back" onClick={() => navigate('/campaigns')}>
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg>
          {t('smd.campaigns')}
        </button>
        <div className="smd-head">
          <div className="smd-m">M</div>
          <div className="smd-head-txt">
            <h1 className="smd-title">{displayName(campaign.name)}</h1>
            <div className="smd-sub">
              {campaign.advertiser}
              <span className={`smd-status ${campaign.status}`}>{statusLabel}</span>
            </div>
          </div>
          <div className="smd-head-logos">
            {platforms.map((p, i) => (
              <span key={`${p}-${i}`} className={disconnectedPlats.has(p) ? 'plat-disconnected' : ''}
                title={disconnectedPlats.has(p) ? t('smd.cabinetDisconnected') : undefined}>
                <PlatformLogo name={p} />
              </span>
            ))}
          </div>
        </div>
      </div>

      {toast && <div className={`smd-toast ${toast.kind}`}>{toast.text}</div>}

      {/* Per-format launch status — live, no reload; failed formats retry independently */}
      {launchJobs && launchJobs.length > 0 && (() => {
        const done = launchJobs.filter(j => j.status === 'launched').length;
        const failed = launchJobs.filter(j => j.status === 'failed').length;
        const running = launchJobs.some(j => j.status === 'pending' || j.status === 'in_progress');
        const JOB_LABEL: Record<string, string> = {
          pending: t('smd.jobQueued'), in_progress: t('smd.jobLaunching'),
          launched: t('smd.jobLaunched'), failed: t('smd.jobFailed'),
        };
        return (
          <section className="smd-card smd-launch">
            <div className="smd-launch-head">
              <h2 className="smd-card-title">{t('smd.launchStatus')}</h2>
              <span className="smd-launch-sub">
                {running && <span className="smd-launch-spin" />}
                {t('smd.nOfMLaunched', { done, total: launchJobs.length })}{failed ? t('smd.nFailedSuffix', { count: failed }) : ''}
              </span>
            </div>
            <div className="smd-launch-list">
              {launchJobs.map((j: any) => (
                <div className={`smd-launch-row ${j.status} ${j.disconnected ? 'is-disconnected' : ''}`} key={j.id}>
                  <span className={`smd-launch-plat ${j.disconnected ? 'plat-disconnected' : ''}`}><PlatformLogo name={j.channel} /></span>
                  <span className="smd-launch-fmt">{j.format_name}</span>
                  <span className={`smd-launch-badge ${j.status}`}>{JOB_LABEL[j.status] || j.status}</span>
                  {j.disconnected
                    ? <span className="smd-ctl fail" title={t('smd.cabinetDisconnectedLong')}>{t('smd.disconnected')}</span>
                    : j.status === 'launched' && (() => {
                    const cs = j.control_status;
                    const ca = j.control_action;              // 'active' | 'paused' | 'update'
                    const isUpdate = ca === 'update';
                    let text: string, kind: string, spin = false, title = '';
                    if (cs === 'pending') { text = t('smd.jobQueued'); kind = 'wait'; }
                    else if (cs === 'in_progress') { text = t(isUpdate ? 'smd.ctlUpdating' : (ca === 'paused' ? 'smd.ctlStopping' : 'smd.ctlStarting')); kind = 'wait'; spin = true; }
                    else if (cs === 'failed') { text = t('smd.jobFailed'); kind = 'fail'; title = j.control_error || ''; }
                    else {
                      // 'done' or no op in flight → reflect the serving state. A status op sets
                      // it (active/paused); an update leaves the current status untouched.
                      const live = (cs === 'done' && !isUpdate) ? ca === 'active' : campaign.status === 'active';
                      text = t(live ? 'smd.stLive' : 'smd.stPaused'); kind = live ? 'live' : 'paused';
                    }
                    return <span className={`smd-ctl ${kind}`} title={title}>{spin && <span className="smd-launch-spin" />}{text}</span>;
                  })()}
                  {j.status === 'failed' && (
                    <button className="smd-launch-retry" disabled={retrying === j.id} onClick={() => retryJob(j.id)}>
                      {retrying === j.id ? '…' : t('smd.retry')}
                    </button>
                  )}
                  {j.status === 'failed' && j.error && (
                    <span className="smd-launch-err" title={j.error}>{humanizeLaunchError(j.error)}</span>
                  )}
                </div>
              ))}
            </div>
          </section>
        );
      })()}

      <div className="smd-grid">
        {/* Settings */}
        <section className="smd-card smd-settings">
          <h2 className="smd-card-title">{t('smd.campaignSettings')}</h2>
          <div className="smd-rows">
            <div className="smd-row"><span className="smd-k">{t('smd.status')}</span>
              <span className="smd-v">{statusLabel}
                {campaign.status === 'active' && (
                  <button className="smd-status-btn pause" disabled={statusBusy} onClick={toggleStatus}>
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>
                    {statusBusy ? '…' : t('smd.pause')}
                  </button>
                )}
                {campaign.status === 'paused' && (
                  <button className="smd-status-btn resume" disabled={statusBusy || !canResume} onClick={toggleStatus}
                    title={canResume ? '' : t('smd.needTwoTools', { count: launchedCount })}>
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                    {statusBusy ? '…' : t('smd.resume')}
                  </button>
                )}
              </span>
            </div>
            <div className="smd-row">
              <span className="smd-k">{t('smd.flight')}</span>
              <span className="smd-v">{fmtDate(campaign.start_date)} – {fmtDate(campaign.end_date)}
                <button className="smd-mini" onClick={() => openEdit('dates')}>{t('smd.edit')}</button>
              </span>
            </div>
            <div className="smd-row">
              <span className="smd-k">{t('smd.budget')}</span>
              <span className="smd-v">{fmtMoney((budget?.total_budget ?? campaign.budget) || 0, ccy)}
                <button className="smd-mini" onClick={() => openEdit('budget')}>{t('smd.edit')}</button>
              </span>
            </div>
            <div className="smd-row">
              <span className="smd-k">{t('smd.platforms')}</span>
              <span className="smd-v">{platforms.map(p => PLATFORM_LABEL[platformKey(p)]).join(', ')}</span>
            </div>
            <div className="smd-row">
              <span className="smd-k">{t('smd.format')}</span>
              <span className="smd-v">{(campaign.types || []).join(', ') || '—'}</span>
            </div>
            <div className="smd-row">
              <span className="smd-k">{t('smd.creativesTexts')}</span>
              <span className="smd-v">{t('smd.uniformAcross')}
                <button className="smd-mini accent" disabled={saving} onClick={openReplace}>{t('smd.replace')}</button>
              </span>
            </div>
          </div>
          <p className="smd-note">{t('smd.relaunchNote')}</p>
        </section>

        {/* Optimization status */}
        <section className="smd-card smd-optim">
          <div className="smd-optim-head">
            <h2 className="smd-card-title">{t('smd.optimization')}</h2>
            <div className="smd-optim-badge">
              <span className="smd-optim-dot" />
              {t('smd.aiOptimizerActive')}
            </div>
          </div>
          <p className="smd-optim-text">
            {t('smd.engineText')}
          </p>
          {campaign.bg_pacing_state === 'THROTTLE' && <div className="smd-flag warn">{t('smd.bgThrottled')}</div>}
          {campaign.bg_pacing_state === 'PAUSED' && <div className="smd-flag danger">{t('smd.bgPaused')}</div>}
          <OptimizationTimeline events={OPTIM_EVENTS} active={activeMarker} onHover={setActiveMarker} />
        </section>
      </div>

      {/* Billing — what runs through the client's balance vs stats-only */}
      <section className="smd-card smd-billing">
        <h2 className="smd-card-title">{t('smd.billing')}</h2>
        {(() => {
          const sense = campaign.spend || 0;
          const ext = budget?.external_spend || 0;
          const ak = budget?.commission || 0;
          const own = budget?.model_type === 'own_accounts';
          const fromBalance = sense + ak + (own ? ext : 0);
          const cell = (label: string, value: number, opts?: { muted?: boolean; tag?: string; total?: boolean }) => (
            <div className={`smd-bill-cell ${opts?.total ? 'total' : ''}`}>
              <div className="smd-bill-label">{label}{opts?.tag && <em className="smd-bill-tag">{opts.tag}</em>}</div>
              <div className={`smd-bill-val ${opts?.muted ? 'muted' : ''}`}>{fmtMoney(value, ccy)}</div>
            </div>
          );
          return (
            <div className="smd-bill-row">
              {cell(t('smd.senseSpend'), sense)}
              {cell(t('smd.externalSpend'), ext, { muted: !own, tag: own ? undefined : t('smd.statsOnly') })}
              {cell(t('smd.colCommission'), ak)}
              {cell(t('smd.chargedFromBalance'), fromBalance, { total: true })}
            </div>
          );
        })()}
      </section>

      {/* Performance — aggregate + per-platform tabs */}
      <section className="smd-card smd-perf">
        <h2 className="smd-card-title">{t('smd.performance')}</h2>
        <div className="smd-tabs">
          <button className={`smd-tab ${activeTab === 'overview' ? 'on' : ''}`} onClick={() => setActiveTab('overview')}>{t('smd.allPlatforms')}</button>
          {platforms.map((p, i) => (
            <button key={i} className={`smd-tab ${activeTab === p ? 'on' : ''}`} onClick={() => setActiveTab(p)}>
              <PlatformLogo name={p} />{PLATFORM_LABEL[platformKey(p)]}
            </button>
          ))}
        </div>

        {activeTab === 'overview' ? (
          <>
            <MetricTiles m={aggWithCommission} ccy={ccy} onSelect={setChartMetric} selected={chartMetric} />
            {trend?.aggregate?.length ? <MetricChart series={trend.aggregate} metric={chartMetric} ccy={ccy} markers={OPTIM_EVENTS} activeMarker={activeMarker} onMarkerHover={setActiveMarker} /> : null}
            <div className="smd-table-wrap">
              <table className="smd-table">
                <thead><tr><th>{t('smd.colPlatform')}</th><th>{t('smd.colImpr')}</th><th>{t('smd.colClicks')}</th><th>CTR</th><th>CPC</th><th>CPM</th><th>{t('smd.colConv')}</th><th>CPA</th><th>{t('smd.colSpend')}</th><th>{t('smd.colCommission')}</th></tr></thead>
                <tbody>
                  {byPlatform.length ? byPlatform.map((r, i) => {
                    const isExt = r.platform !== 'sense';
                    const comm = isExt ? (r.spend || 0) * (budget?.commission_percent || 0) / 100 : null;
                    return (
                    <tr key={i}>
                      <td className="smd-plat-cell"><PlatformLogo name={r.platform} />{PLATFORM_LABEL[platformKey(r.platform)]}</td>
                      <td>{fmtInt(r.impressions)}</td><td>{fmtInt(r.clicks)}</td>
                      <td>{(r.ctr || 0).toFixed(2)}%</td>
                      <td>{fmtMoney(r.cpc || 0, r.currency || ccy)}</td>
                      <td>{fmtMoney(r.cpm || 0, r.currency || ccy)}</td>
                      <td>{r.conversions == null ? '—' : fmtInt(r.conversions)}</td>
                      <td>{r.cpa ? fmtMoney(r.cpa, r.currency || ccy) : '—'}</td>
                      <td>{fmtMoney(r.spend, r.currency || ccy)}</td>
                      <td>{comm == null ? '—' : fmtMoney(comm, ccy)}</td>
                    </tr>
                    );
                  }) : (
                    <tr><td colSpan={10} className="smd-empty">{t(statsLoading ? 'smd.loadingShort' : 'smd.noPerfData')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="smd-note">{t('smd.convOwnNote')}</p>
          </>
        ) : (() => {
          // The tab value can arrive as a PLATFORM key ('google') or a CHANNEL key
          // ('google_ads') depending on how the campaign object was navigated here (the
          // campaigns list carries the backend channel key, a draft-derived object carries
          // the normalised platform key). The stats/trend payloads are ALWAYS keyed by
          // channel, so normalise the lookup — otherwise the Google tab reads channels['google']
          // (nonexistent) and shows "0 formats" while the data sits under 'google_ads'.
          const chKey = activeTab === 'google' ? 'google_ads' : activeTab;
          const chData = stats?.channels?.[chKey] ?? stats?.channels?.[activeTab];
          const formats: any[] = chData?.formats || [];
          const chTrend = trend?.by_channel?.[chKey] ?? trend?.by_channel?.[activeTab];
          const chEvents = OPTIM_EVENTS.filter(e => e.channel === chKey || e.channel === activeTab);
          return (
            <div className="smd-platform-detail">
              <div className="smd-platform-head">
                <PlatformLogo name={activeTab} /> {PLATFORM_LABEL[platformKey(activeTab)]} — {t('smd.nFormats', { count: formats.length })}
              </div>
              {chData?.total && (
                <>
                  <MetricTiles m={chData.total} ccy={ccy} onSelect={setChartMetric} selected={chartMetric} />
                  {chTrend?.length ? <MetricChart series={chTrend} metric={chartMetric} ccy={ccy} markers={chEvents} activeMarker={activeMarker} onMarkerHover={setActiveMarker} /> : null}
                </>
              )}
              {statsLoading ? <div className="smd-empty">{t('smd.loadingShort')}</div>
                : formats.length
                  ? formats.map((f, i) => <FormatCard key={f.id || i} fmt={f} ccy={ccy} commissionPct={budget?.commission_percent || 0} external={activeTab !== 'sense'} />)
                  : <div className="smd-empty">{t('smd.noPlatformData')}</div>}
            </div>
          );
        })()}
      </section>

      {/* Edit modal */}
      {editKind && (
        <div className="smd-modal-overlay" onClick={() => !saving && setEditKind(null)}>
          <div className="smd-modal" onClick={e => e.stopPropagation()}>
            <h3 className="smd-modal-title">{t(editKind === 'budget' ? 'smd.editBudget' : 'smd.editFlight')}</h3>
            {editKind === 'budget' ? (
              <label className="smd-field">
                <span>{t('smd.totalBudget', { ccy })}</span>
                <input type="number" min="0" value={fBudget} onChange={e => setFBudget(e.target.value)} />
              </label>
            ) : (
              <div className="smd-field-row">
                <label className="smd-field">
                  <span>{t('smd.start')}</span>
                  <input type="date" value={fStart || ''} disabled={startAlreadyBegun}
                         onChange={e => setFStart(e.target.value)} />
                </label>
                <label className="smd-field"><span>{t('smd.end')}</span><input type="date" value={fEnd || ''} onChange={e => setFEnd(e.target.value)} /></label>
              </div>
            )}
            <p className="smd-modal-note">
              {editKind === 'dates' && startAlreadyBegun
                ? t('smd.startLocked')
                : t('smd.appliedToAll')}
            </p>
            <div className="smd-modal-actions">
              <button className="smd-btn ghost" onClick={() => setEditKind(null)} disabled={saving}>{t('common.cancel')}</button>
              <button className="smd-btn primary" onClick={saveEdit} disabled={saving}>{t(saving ? 'smd.applying' : 'smd.apply')}</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SenseMaxDashboardPage;
